import React, { useMemo, useState } from "react";
import { Button, Tooltip } from "cprime-foundational-components";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import "./SharePage.scss";
import UseScreenSize from "../../../shared/useScreenSize";
import { axiosInstance } from "../../../utils/axiosInstance";
// import { AuthContext } from "../../../components/Authenticator/Authenticator";

interface SharePageProps {
  sharePayload: Record<string, any>;
  shareEnv: string;
}

const SharePage: React.FC<SharePageProps> = ({ sharePayload, shareEnv }) => {
  const screenSize = UseScreenSize();
  const isTabletOrMobile = screenSize.width < 945;
  //   const authObject = useContext(AuthContext);

  //   const isCascadingFilterSwitchChecked = useAppSelector(
  //     (state) => state.toggleFilter.isCascadingFilterSwitchChecked
  //   );
  //   const isSidebarExpand = useAppSelector(
  //     (state) => state.toggleFilter.isSidebarExpand
  //   );

  //   const {
  //     selectedBrand,
  //     viewBy
  //   } = useAppSelector(state => state.selectedSideMenu);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const buildShareUrl = (linkId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("linkId", linkId);
    if (shareEnv) {
      url.searchParams.set("env", shareEnv);
    }
    return url.toString();
  };

  const createShareableLink = async () => {
    const response = await axiosInstance.post(
      "/onboard/dashboard/share",
      sharePayload,
      { params: { env: shareEnv } },
    );
    const linkId = response?.data?.data?.linkId;
    if (linkId) {
      return buildShareUrl(linkId);
    }
    return window.location.href;
  };

  const handleOn = (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    setCopySuccess(false);
    setAnchorEl(event.currentTarget as HTMLElement);

    setIsGenerating(true);
    createShareableLink()
      .then((url) => {
        setGeneratedUrl(url);
      })
      .catch(() => {
        setGeneratedUrl(window.location.href);
      })
      .finally(() => {
        setIsGenerating(false);
      });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const popupWidth = 344;
  const popupTop = useMemo(() => {
    if (!anchorEl) return 0;
    return anchorEl.getBoundingClientRect().bottom + 8;
  }, [anchorEl]);

  const popupLeft = useMemo(() => {
    if (!anchorEl) return 0;
    const anchorRect = anchorEl.getBoundingClientRect();
    const left = anchorRect.right - popupWidth;
    return Math.max(16, left);
  }, [anchorEl]);

  const displayUrl = generatedUrl || window.location.href;

  const handleCopyLink = async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopySuccess(true);
    } catch {
      setCopySuccess(false);
    }
  };

  return (
    <div className="share-root">
      {isTabletOrMobile ? (
        <Tooltip title={<div>Share</div>} disabled={false} placement="bottom">
          <button className="dIcons" onClick={handleOn}>
            <InsertLinkIcon style={{ height: "24px", width: "24px" }} />
          </button>
        </Tooltip>
      ) : (
        <Button
          sx={{
            width: 94,
            minWidth: "94px",
            height: 40,
            borderRadius: "4px",
            gap: "8px",
            paddingTop: "12px",
            paddingRight: "16px",
            paddingBottom: "12px",
            paddingLeft: "16px",
            borderWidth: "1px",
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: "14px",
          }}
          variant="tertiary"
          size="small"
          onClick={handleOn}
          endIcon={<InsertLinkIcon style={{ height: "12px", width: "12px" }} />}
        >
          Share
        </Button>
      )}

      {anchorEl && (
        <div className="share-overlay" role="presentation">
          <button
            type="button"
            className="share-overlay-backdrop"
            onClick={handleClose}
            aria-label="Close share dialog"
          />
          <div
            className="share-menu-container"
            style={{ top: popupTop, left: popupLeft, width: `${popupWidth}px` }}
            role="dialog"
            aria-label="Share"
          >
            <div className="share-menu-header">
              <span className="share-title">Share</span>
              <button
                type="button"
                className="share-close"
                onClick={handleClose}
                aria-label="Close share"
              >
                <CloseOutlinedIcon sx={{ width: 18, height: 18 }} />
              </button>
            </div>

            <div className="share-description">
              Share your page view with other users.
            </div>

            <div className="share-description">
              Shared configurations will replicate your settings and will not
              impact your saved views. Users need to save this configuration as
              their own.
            </div>

            <div className="share-link-box" title={generatedUrl}>
              <span className="share-link-text">
                {isGenerating ? "Generating link..." : displayUrl}
              </span>
            </div>

            <button
              type="button"
              className={`share-copy-link ${copySuccess ? "share-copy-link-success" : ""}`}
              onClick={handleCopyLink}
              disabled={!generatedUrl || isGenerating}
            >
              <span>{copySuccess ? "Link Copied!" : "Copy Link"}</span>
              {copySuccess ? (
                <CheckCircleOutlineOutlinedIcon
                  sx={{ width: 14, height: 14 }}
                />
              ) : (
                <ContentCopyOutlinedIcon sx={{ width: 14, height: 14 }} />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharePage;
